/*
 * Pickup preference prefill (VIS-836).
 *
 * Pre-selects the visitor's favorite store as the pickup location at checkout:
 * fire-and-forget POST of the cart token + favorite store handle to the app
 * proxy (endpoint declared by pickup-preference.liquid), where the app resolves
 * the linked Shopify location and writes the cart's delivery preference through
 * the Storefront API. Checkout then opens with that store pre-selected and
 * listed first.
 *
 * The preference is a prefill, pushed once per (cart, favorite handle): a
 * localStorage marker remembers the last pushed pair, so later cart edits never
 * re-push and the buyer's own selection at checkout survives. A new cart or a
 * changed favorite invalidates the marker and pushes again. Works for guests
 * too — the favorite lives in localStorage and the preference is cart-scoped.
 */

import { ThemeEvents } from '@theme/events';
import { getFavorite } from '@theme/store-favorites';

const SYNCED_KEY = 'vo:pickupPreferenceSynced';

/** @returns {{ endpoint: string, itemCount?: number } | null} */
function config() {
  const element = document.querySelector('[data-pickup-preference-config]');
  if (!element) return null;
  try {
    const parsed = JSON.parse(element.textContent);
    return parsed && typeof parsed.endpoint === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

/** @returns {string | null} */
function getSynced() {
  try {
    return localStorage.getItem(SYNCED_KEY) || null;
  } catch {
    return null;
  }
}

/** @param {string} marker */
function setSynced(marker) {
  try {
    localStorage.setItem(SYNCED_KEY, marker);
  } catch {
    // Private mode / quota — worst case the preference is pushed again, which
    // only re-applies the same prefill.
  }
}

/**
 * Fire-and-forget: any failure is retried by the next trigger.
 * @param {{ useItemCountHint?: boolean }} [options] - useItemCountHint skips
 *   the /cart.js fetch when the Liquid-rendered item count is 0, sparing an
 *   uncached request on (most) page views. The hint can be stale on cached
 *   guest pages, so it is only trusted on page load: a stale 0 merely defers
 *   the first push to the next cart event.
 */
async function push({ useItemCountHint = false } = {}) {
  const settings = config();
  const endpoint = settings?.endpoint;
  const favorite = getFavorite();
  if (!endpoint || !favorite) return;
  if (useItemCountHint && settings.itemCount === 0) return;

  let cart;
  try {
    cart = await (await fetch('/cart.js')).json();
  } catch {
    return;
  }
  // The token addresses the Storefront API cart verbatim (?key= suffix included).
  if (!cart?.token || !cart.item_count) return;

  const marker = `${cart.token}::${favorite.handle}`;
  if (getSynced() === marker) return;

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cart: cart.token, handle: favorite.handle }),
    });
    if (!response.ok) return;
    // The marker is only set once the preference is actually written: a
    // located: false store (not linked to a pickup location yet) retries on
    // the next cart event instead of being latched off, so a link added
    // mid-rollout applies to active sessions too. The retry is cheap — the
    // app caches the lookup.
    const result = await response.json();
    if (result.located) setSynced(marker);
  } catch {
    // ignore — fire-and-forget
  }
}

let inFlight = false;
let dirty = false;

/**
 * Serializes concurrent triggers; a trigger landing mid-push re-runs once.
 * @param {{ useItemCountHint?: boolean }} [options]
 */
async function sync(options) {
  if (inFlight) {
    dirty = true;
    return;
  }
  inFlight = true;
  try {
    await push(options);
  } finally {
    inFlight = false;
    if (dirty) {
      dirty = false;
      sync();
    }
  }
}

sync({ useItemCountHint: true });
document.addEventListener(ThemeEvents.cartUpdate, () => sync());
window.addEventListener('store-favorite:changed', () => sync());
